import { useCallback, useEffect, useState } from 'react';
import { MdExpandMore } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';

import CardView from '../../..';
import { fetchMoreAssets } from 'features/assets/assetsTableSlice';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';

const noop = () => {};

const CategoriesAssetCard = ({
    category,
    allCategories,
    searchString,
    isLoadingAssets,
    readOnlyMode,
    assetType,
    assetPayload,
    setAssetPayload,
    expanded,
    handleOpenCategory,
    selectedAssetCategory,
    setNoCategoriesAsset = noop,
}) => {
    //#region Constants
    const dispatch = useDispatch();
    //#endregion Constants

    //#region Hooks
    //#endregion Hooks

    //#region State
    const [filteredAssets, setFilteredAssets] = useState([]);
    const [filteredAssetsPageInfo, setFilteredAssetsPageInfo] = useState({});
    const [isLoadingCategoryAssets, setIsLoadingCategoryAssets] = useState(category?.asset_count > 0);
    //#endregion State

    //#region Selectors
    //#endregion Selectors

    //#region Refs
    //#endregion Refs

    const fetchCategoryAsset = useCallback(
        async (categoryId) => {
            setIsLoadingCategoryAssets(true);
            await dispatch(activateLoading());
            try {
                const categoriesAssets = await dispatch(
                    fetchMoreAssets({
                        ...assetPayload,
                        startIndex: 0,
                        stopIndex: 14,
                        searchString: searchString,
                        assetCategoryId: categoryId,
                    })
                );
                const categoryAssets = categoriesAssets.payload?.assets ?? [];
                setFilteredAssetsPageInfo(categoriesAssets.payload?.pageInfo ?? {});
                if (categoryAssets.length > 0) {
                    setNoCategoriesAsset(false);
                }
                setFilteredAssets(categoryAssets);
            } finally {
                await dispatch(deactivateLoading());
                setIsLoadingCategoryAssets(false);
            }
        },
        [dispatch, assetPayload, searchString, setNoCategoriesAsset]
    );

    //#region Effects
    useEffect(() => {
        if (category && category?.asset_count > 0) {
            fetchCategoryAsset(category?.category_id);
        } else {
            setFilteredAssets([]);
            setFilteredAssetsPageInfo({});
            setIsLoadingCategoryAssets(false);
        }
    }, [category, allCategories, fetchCategoryAsset]);
    //#endregion Effects

    const hasFilteredAssets = filteredAssets?.length > 0;
    const isSelectedCategory = selectedAssetCategory?.some(
        (selectedCategory) => selectedCategory.category_id === category?.category_id
    );
    const showEmptyState = isSelectedCategory && !isLoadingCategoryAssets && !hasFilteredAssets;
    const emptyState = (
        <Typography
            variant="body2"
            sx={{
                padding: '16px',
                color: 'text.secondary',
            }}
        >
            No {assetType === 'procedures' ? 'procedures' : 'assets'} found for the selected categories.
        </Typography>
    );

    //#region Render time calcs
    //#endregion Render time calcs

    //#region Render
    return (
        (hasFilteredAssets || showEmptyState) &&
        (selectedAssetCategory?.length === 1 ? (
            hasFilteredAssets ? (
                <CardView
                    assets={filteredAssets}
                    pageInfo={filteredAssetsPageInfo}
                    searchString={searchString}
                    isLoadingAssets={isLoadingAssets}
                    readOnly={readOnlyMode}
                    assetType={assetType}
                    assetPayload={assetPayload}
                    setAssetPayload={setAssetPayload}
                    accordianGrid={true}
                    headerDisplay={false}
                    categoryId={category?.category_id}
                    setFilteredAssets={setFilteredAssets}
                />
            ) : (
                emptyState
            )
        ) : (
            <Accordion
                id={`category-${category?.category_id}`}
                expanded={expanded.some((cat) => cat === category?.category_id)}
                onChange={handleOpenCategory(category?.category_id)}
            >
                <AccordionSummary
                    sx={{
                        backgroundColor: '#efefef',
                    }}
                    expandIcon={<MdExpandMore />}
                >
                    {category?.display_name}
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '8px !important' }}>
                    <Box
                        id={`assetCards-${category.category_id}`}
                        sx={{
                            overflow: 'auto !important',
                        }}
                    >
                        {hasFilteredAssets ? (
                            <CardView
                                assets={filteredAssets}
                                pageInfo={filteredAssetsPageInfo}
                                searchString={searchString}
                                isLoadingAssets={isLoadingAssets}
                                readOnly={readOnlyMode}
                                assetType={assetType}
                                assetPayload={assetPayload}
                                setAssetPayload={setAssetPayload}
                                accordianGrid={true}
                                headerDisplay={false}
                                categoryId={category?.category_id}
                                setFilteredAssets={setFilteredAssets}
                            />
                        ) : (
                            emptyState
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>
        ))
    );
    //#endregion Render
};

export { CategoriesAssetCard };
